import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

interface ShareSheetProps {
  isOpen: boolean
  onClose: () => void
  title: string
  text?: string
  url: string
  resourceLabel?: 'report' | 'article'
  onShareAction?: (method: 'copy_link' | 'native_share') => void
}

type ShareAction = {
  id: string
  label: string
  icon: React.ReactNode
  onClick: () => void | Promise<void>
}

type ShareActionTone = 'neutral' | 'copied' | 'email' | 'whatsapp' | 'x' | 'linkedin'

const CANONICAL_URL_PREFIX_REGEX = /^https?:\/\/(www\.)?/i

function getActionTone(actionId: string, copied: boolean): ShareActionTone {
  if (actionId === 'copy-link' && copied) return 'copied'
  if (actionId === 'email') return 'email'
  if (actionId === 'whatsapp') return 'whatsapp'
  if (actionId === 'x') return 'x'
  if (actionId === 'linkedin') return 'linkedin'
  return 'neutral'
}

function getActionButtonClasses(tone: ShareActionTone) {
  switch (tone) {
    case 'copied':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700 shadow-[0_20px_38px_-28px_rgba(16,185,129,0.7)] dark:border-emerald-400/25 dark:bg-emerald-400/10 dark:text-emerald-300'
    case 'email':
      return 'border-sky-200/90 bg-sky-50/95 text-slate-900 hover:border-sky-300 hover:bg-sky-100/90 dark:border-sky-400/20 dark:bg-sky-400/[0.12] dark:text-sky-50 dark:hover:border-sky-300/30 dark:hover:bg-sky-400/[0.16]'
    case 'whatsapp':
      return 'border-emerald-200/90 bg-emerald-50/95 text-slate-900 hover:border-emerald-300 hover:bg-emerald-100/90 dark:border-emerald-400/20 dark:bg-emerald-400/[0.12] dark:text-emerald-50 dark:hover:border-emerald-300/30 dark:hover:bg-emerald-400/[0.16]'
    case 'x':
      return 'border-slate-300/90 bg-slate-100/95 text-slate-950 hover:border-slate-400 hover:bg-slate-200/90 dark:border-white/10 dark:bg-white/[0.05] dark:text-white dark:hover:border-white/20 dark:hover:bg-white/[0.08]'
    case 'linkedin':
      return 'border-[#0A66C2]/20 bg-[#0A66C2]/[0.08] text-slate-900 hover:border-[#0A66C2]/30 hover:bg-[#0A66C2]/[0.12] dark:border-[#0A66C2]/35 dark:bg-[#0A66C2]/[0.16] dark:text-blue-50 dark:hover:bg-[#0A66C2]/[0.2]'
    default:
      return 'border-slate-200 bg-white/80 text-slate-700 hover:border-slate-300 hover:bg-white dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-200 dark:hover:border-white/20 dark:hover:bg-white/[0.05]'
  }
}

function getActionIconClasses(tone: ShareActionTone) {
  switch (tone) {
    case 'copied':
      return 'bg-emerald-500 text-white'
    case 'email':
      return 'bg-sky-500 text-white shadow-[0_14px_28px_-16px_rgba(14,165,233,0.85)]'
    case 'whatsapp':
      return 'bg-[#25D366] text-white shadow-[0_14px_28px_-16px_rgba(37,211,102,0.9)]'
    case 'x':
      return 'bg-slate-950 text-white shadow-[0_14px_28px_-16px_rgba(15,23,42,0.95)] dark:bg-white dark:text-slate-950'
    case 'linkedin':
      return 'bg-[#0A66C2] text-white shadow-[0_14px_28px_-16px_rgba(10,102,194,0.9)]'
    default:
      return 'bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-white'
  }
}

function fallbackCopyText(text: string) {
  const textArea = document.createElement('textarea')
  textArea.value = text
  textArea.setAttribute('readonly', '')
  textArea.style.position = 'fixed'
  textArea.style.opacity = '0'
  textArea.style.pointerEvents = 'none'
  textArea.style.left = '-9999px'
  textArea.style.top = '0'

  document.body.appendChild(textArea)

  const selection = window.getSelection()
  const previousRange = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null

  textArea.focus()
  textArea.select()
  textArea.setSelectionRange(0, textArea.value.length)

  const copied = document.execCommand('copy')

  document.body.removeChild(textArea)

  if (selection) {
    selection.removeAllRanges()
    if (previousRange) {
      selection.addRange(previousRange)
    }
  }

  if (!copied) {
    throw new Error('Unable to copy share link.')
  }
}

export default function ShareSheet({
  isOpen,
  onClose,
  title,
  text,
  url,
  resourceLabel = 'report',
  onShareAction,
}: ShareSheetProps) {
  const [copied, setCopied] = useState(false)
  const [copyHint, setCopyHint] = useState<string | null>(null)
  const copyResetTimeoutRef = useRef<number | null>(null)
  const shareUrlInputRef = useRef<HTMLInputElement | null>(null)
  const nativeShareAvailable = typeof navigator !== 'undefined' && typeof navigator.share === 'function'
  const sheetTitle = resourceLabel === 'article' ? 'Share article' : 'Share report'
  const previewUrl = url.replace(CANONICAL_URL_PREFIX_REGEX, '')
  const shareMessage = useMemo(() => {
    return [text?.trim() || title.trim(), url].filter(Boolean).join('\n\n')
  }, [text, title, url])

  useEffect(() => {
    if (!isOpen) {
      setCopied(false)
      setCopyHint(null)
      if (copyResetTimeoutRef.current) {
        window.clearTimeout(copyResetTimeoutRef.current)
        copyResetTimeoutRef.current = null
      }
      return
    }

    const previousOverflow = document.body.style.overflow
    const previousModalState = document.body.getAttribute('data-modal-open')
    document.body.style.overflow = 'hidden'
    document.body.setAttribute('data-modal-open', 'true')
    window.dispatchEvent(new Event('modal-open-changed'))

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)

    return () => {
      document.body.style.overflow = previousOverflow
      if (previousModalState === null) {
        document.body.removeAttribute('data-modal-open')
      } else {
        document.body.setAttribute('data-modal-open', previousModalState)
      }
      window.dispatchEvent(new Event('modal-open-changed'))
      document.removeEventListener('keydown', handleEscape)
      if (copyResetTimeoutRef.current) {
        window.clearTimeout(copyResetTimeoutRef.current)
        copyResetTimeoutRef.current = null
      }
    }
  }, [isOpen, onClose])

  const resetCopiedStateSoon = () => {
    if (copyResetTimeoutRef.current) {
      window.clearTimeout(copyResetTimeoutRef.current)
    }

    copyResetTimeoutRef.current = window.setTimeout(() => {
      setCopied(false)
      copyResetTimeoutRef.current = null
    }, 1800)
  }

  const handleCopyLink = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(url)
        } catch (clipboardError) {
          console.debug('Clipboard API copy failed, falling back to execCommand:', clipboardError)
          fallbackCopyText(url)
        }
      } else {
        fallbackCopyText(url)
      }

      setCopyHint(null)
      setCopied(true)
      onShareAction?.('copy_link')
      resetCopiedStateSoon()
    } catch (error) {
      console.error('Copy share link failed:', error)
      setCopyHint('Copy is blocked here. Press Cmd+C / Ctrl+C to copy manually.')
      shareUrlInputRef.current?.focus()
      shareUrlInputRef.current?.select()
    }
  }

  const openExternalTarget = (targetUrl: string) => {
    onShareAction?.('native_share')
    onClose()
    if (targetUrl.startsWith('mailto:')) {
      window.location.href = targetUrl
      return
    }

    window.open(targetUrl, '_blank', 'noopener,noreferrer')
  }

  const handleMore = async () => {
    if (!nativeShareAvailable) return

    try {
      await navigator.share({
        title,
        text,
        url,
      })
      onShareAction?.('native_share')
      onClose()
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        return
      }

      console.error('Native share failed:', error)
      alert('Unable to open share options.')
    }
  }

  const actions: ShareAction[] = [
    {
      id: 'copy-link',
      label: copied ? 'Copied' : 'Copy link',
      icon: copied ? <CheckIcon /> : <CopyIcon />,
      onClick: handleCopyLink,
    },
    {
      id: 'email',
      label: 'Email',
      icon: <EmailIcon />,
      onClick: () => {
        const emailUrl = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(shareMessage)}`
        openExternalTarget(emailUrl)
      },
    },
    {
      id: 'whatsapp',
      label: 'WhatsApp',
      icon: <WhatsAppIcon />,
      onClick: () => {
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`
        openExternalTarget(whatsappUrl)
      },
    },
    {
      id: 'x',
      label: 'X',
      icon: <XIcon />,
      onClick: () => {
        const xUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text?.trim() || title)}`
        openExternalTarget(xUrl)
      },
    },
    {
      id: 'linkedin',
      label: 'LinkedIn',
      icon: <LinkedInIcon />,
      onClick: () => {
        const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
        openExternalTarget(linkedInUrl)
      },
    },
  ]

  if (nativeShareAvailable) {
    actions.push({
      id: 'more',
      label: 'More',
      icon: <MoreIcon />,
      onClick: handleMore,
    })
  }

  if (!isOpen) return null

  return createPortal(
    <div className="fixed inset-0 z-[120]">
      <div
        className="absolute inset-0 bg-slate-950/45 backdrop-blur-md"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="absolute inset-x-0 bottom-0 flex min-h-full items-end justify-center px-0 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-2 sm:items-center sm:p-6">
        <section
          role="dialog"
          aria-modal="true"
          aria-labelledby="share-sheet-title"
          data-testid="share-sheet-dialog"
          className="relative flex w-full max-w-lg max-h-[calc(100dvh-0.5rem)] flex-col overflow-hidden rounded-t-[28px] border border-white/10 bg-white/92 shadow-[0_32px_100px_-32px_rgba(15,23,42,0.55)] backdrop-blur-2xl dark:border-white/10 dark:bg-slate-950/92 sm:max-h-[calc(100dvh-3rem)] sm:rounded-[32px]"
        >
          <div className="mx-auto mt-3 h-1.5 w-14 rounded-full bg-slate-200 dark:bg-white/15 sm:hidden" />

          <div
            data-testid="share-sheet-scroll-region"
            className="min-h-0 overflow-y-auto overscroll-contain px-5 pb-[calc(env(safe-area-inset-bottom)+1.25rem)] pt-4 sm:px-7 sm:pb-7 sm:pt-6"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="relative w-full overflow-hidden rounded-[28px] border border-white/10 bg-slate-950/[0.82] px-5 py-5 shadow-[0_32px_70px_-40px_rgba(15,23,42,0.92)] backdrop-blur-xl sm:rounded-[30px] sm:px-6 sm:py-6">
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(96,165,250,0.26),transparent_36%),radial-gradient(circle_at_top_left,rgba(168,85,247,0.28),transparent_42%),linear-gradient(135deg,rgba(15,23,42,0.96),rgba(30,41,59,0.84))]" />
                <div className="relative flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p
                      id="share-sheet-title"
                      className="text-[0.72rem] font-semibold uppercase tracking-[0.34em] text-white/55"
                    >
                      {sheetTitle}
                    </p>
                    <h2
                      data-share-title="primary"
                      className="mt-3 text-[1.6rem] font-semibold leading-[1.05] tracking-[-0.03em] text-white drop-shadow-[0_10px_30px_rgba(15,23,42,0.3)] sm:text-[2.35rem]"
                    >
                      {title}
                    </h2>
                  </div>

                  <button
                    type="button"
                    onClick={onClose}
                    className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white/70 shadow-[0_18px_35px_-24px_rgba(15,23,42,0.9)] transition-all hover:border-white/25 hover:bg-white/15 hover:text-white"
                    aria-label="Close share sheet"
                  >
                    <CloseIcon />
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-5 rounded-3xl border border-slate-200/80 bg-slate-50/90 p-4 dark:border-white/10 dark:bg-white/[0.04]">
              <p className="text-xs font-medium uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500">
                Canonical link
              </p>
              <input
                ref={shareUrlInputRef}
                readOnly
                value={url}
                onFocus={(event) => event.currentTarget.select()}
                className="mt-2 w-full rounded-2xl border border-slate-200 bg-white/90 px-3 py-2 text-sm font-medium text-slate-700 outline-none ring-0 transition focus:border-slate-300 dark:border-white/10 dark:bg-slate-900/80 dark:text-slate-100 dark:focus:border-white/20"
                aria-label="Canonical share link"
              />
              <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
                {copyHint || `Copy the link or share it directly through your preferred app. ${previewUrl}`}
              </p>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {actions.map((action) => {
                const tone = getActionTone(action.id, copied)

                return (
                  <button
                    key={action.id}
                    type="button"
                    data-share-action={action.id}
                    data-share-tone={tone}
                    onClick={() => void action.onClick()}
                    className={`group flex min-h-[96px] flex-col items-start justify-between rounded-3xl border px-3.5 py-3.5 text-left transition-all hover:-translate-y-0.5 hover:shadow-lg sm:min-h-[110px] sm:px-4 sm:py-4 ${getActionButtonClasses(tone)}`}
                  >
                    <span
                      className={`inline-flex h-10 w-10 items-center justify-center rounded-2xl sm:h-11 sm:w-11 ${getActionIconClasses(tone)}`}
                    >
                      {action.icon}
                    </span>
                    <span className="mt-3 text-sm font-semibold tracking-tight sm:mt-4">
                      {action.label}
                    </span>
                  </button>
                )
              })}
            </div>
          </div>
        </section>
      </div>
    </div>,
    document.body,
  )
}

function CopyIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" d="M8 7V5a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2M8 7h8a2 2 0 012 2v8" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="m5 13 4 4L19 7" />
    </svg>
  )
}

function EmailIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M4.75 5h14.5A1.75 1.75 0 0 1 21 6.75v10.5A1.75 1.75 0 0 1 19.25 19H4.75A1.75 1.75 0 0 1 3 17.25V6.75A1.75 1.75 0 0 1 4.75 5Zm14.28 1.75H4.97L12 11.48l7.03-4.73ZM4.75 17.5h14.5a.25.25 0 0 0 .25-.25V8.2l-7.08 4.76a.75.75 0 0 1-.84 0L4.5 8.2v9.05c0 .14.11.25.25.25Z" />
    </svg>
  )
}

function WhatsAppIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.04 2.5a9.47 9.47 0 0 0-8.13 14.34L2.5 21.5l4.78-1.38A9.5 9.5 0 1 0 12.04 2.5Zm0 17.17a7.67 7.67 0 0 1-3.91-1.07l-.28-.17-2.84.82.83-2.77-.18-.29a7.69 7.69 0 1 1 6.38 3.48Zm4.21-5.73c-.23-.11-1.34-.66-1.55-.74-.2-.07-.35-.11-.5.11-.14.22-.56.74-.68.9-.13.15-.25.17-.48.06-.22-.11-.93-.34-1.77-1.08-.65-.59-1.09-1.31-1.22-1.53-.13-.22-.01-.34.09-.45.1-.1.22-.25.34-.37.11-.13.15-.22.22-.37.07-.15.03-.28-.02-.4-.06-.11-.5-1.21-.7-1.66-.18-.43-.36-.37-.5-.37h-.43c-.15 0-.4.06-.61.28-.21.22-.8.78-.8 1.9 0 1.12.81 2.21.92 2.36.11.15 1.59 2.42 3.86 3.39 2.28.98 2.28.65 2.69.61.41-.03 1.34-.55 1.53-1.08.18-.54.18-1 .13-1.09-.06-.08-.21-.13-.44-.24Z" />
    </svg>
  )
}

function XIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.901 2H21l-6.868 7.848L22 22h-6.17l-4.832-7.403L4.63 22H2.53l7.346-8.396L2 2h6.327l4.367 6.74L18.9 2Zm-1.085 18h1.163L7.68 3.895H6.43L17.816 20Z" />
    </svg>
  )
}

function LinkedInIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
      <path d="M5.36 7.43a2.07 2.07 0 1 0 0-4.14 2.07 2.07 0 0 0 0 4.14ZM3.7 8.98h3.34V19.7H3.7V8.98Zm5.44 0h3.2v1.47h.05c.45-.85 1.54-1.75 3.17-1.75 3.39 0 4.01 2.23 4.01 5.12v5.88h-3.34v-5.2c0-1.24-.02-2.83-1.72-2.83-1.72 0-1.99 1.35-1.99 2.74v5.29H9.14V8.98Z" />
    </svg>
  )
}

function MoreIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" d="M6.75 12a.75.75 0 1 1 0 0Zm5.25 0a.75.75 0 1 1 0 0Zm5.25 0a.75.75 0 1 1 0 0Z" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" d="M6 6 18 18M18 6 6 18" />
    </svg>
  )
}
